import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { createInvitation } from "../../api/onboarding";
import { useApiError } from "../../hooks/useApiError";
import { NeuButton, NeuCard, NeuInput, NeuSelect } from "../../components/neu/Neu";
import { BranchChips } from "../../components/neu/BranchChips";
import { InviteList } from "./InviteList";
import { SeatUpgradeDialog } from "./SeatUpgradeDialog";

const ROLES = ["employee", "branch_manager", "system_admin"];

export const InvitePanel = ({ invites, branches, reload }) => {
  const { t } = useTranslation();
  const describe = useApiError();
  const [form, setForm] = useState({ email: "", role: "employee", valid_hours: 48, branch_ids: [] });
  const [seatOpen, setSeatOpen] = useState(false);

  const create = async () => {
    const email = form.email.trim();
    const hours = Number(form.valid_hours);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return toast.error(t("invites.invalidEmail"));
    if (!form.branch_ids.length) return toast.error(t("invites.needBranch"));
    if (!hours || hours < 1 || hours > 168) return toast.error(t("invites.invalidHours"));
    try {
      const res = await createInvitation({ ...form, email, valid_hours: hours });
      setForm({ email: "", role: "employee", valid_hours: 48, branch_ids: [] });
      toast.success(t(res?.email_sent ? "invites.emailed" : "invites.created", { email }));
      reload();
    } catch (err) {
      if (err?.response?.data?.detail?.code === "plan_limit_reached") return setSeatOpen(true);
      toast.error(describe(err));
    }
  };

  const single = form.role === "employee";
  const toggleBranch = (id) =>
    setForm((f) => (single
      ? { ...f, branch_ids: f.branch_ids[0] === id ? [] : [id] }
      : { ...f, branch_ids: f.branch_ids.includes(id) ? f.branch_ids.filter((b) => b !== id) : [...f.branch_ids, id] }));

  return (
    <NeuCard data-testid="invite-panel">
      <div className="grid gap-3 sm:grid-cols-3">
        <NeuInput
          data-testid="invite-email"
          type="email"
          placeholder={t("invites.email")}
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
        />
        <NeuSelect data-testid="invite-role" value={form.role} onChange={(e) => { const role = e.target.value; setForm((f) => ({ ...f, role, branch_ids: role === "employee" ? f.branch_ids.slice(0, 1) : f.branch_ids })); }}>
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {t(`roles.${r}`)}
            </option>
          ))}
        </NeuSelect>
        <NeuInput
          data-testid="invite-hours"
          type="number"
          min={1}
          max={168}
          placeholder={t("invites.validity")}
          value={form.valid_hours}
          onChange={(e) => setForm({ ...form, valid_hours: e.target.value })}
        />
      </div>
      <div className="mt-3">
        <p className="label-xs mb-2">{single ? t("invites.branchOne") : t("employees.branches")}</p>
        <div className="flex flex-wrap gap-2">
          <BranchChips branches={branches} selected={form.branch_ids} onToggle={toggleBranch} prefix="invite-branch" />
        </div>
      </div>
      <NeuButton variant="primary" className="mt-4" data-testid="invite-create" onClick={create}>
        {t("invites.create")}
      </NeuButton>
      <InviteList invites={invites} reload={reload} />
      <SeatUpgradeDialog open={seatOpen} onOpenChange={setSeatOpen} />
    </NeuCard>
  );
};
